import React from 'react';
import { Card } from '../types';
import { HoloCard } from './HoloCard';
import { Heart, Plus, Minus, Layers, Star, CircleDollarSign } from 'lucide-react';
import { useCollection } from '../context/CollectionContext';
import { useLanguage } from '../context/LanguageContext';
import { soundEffects } from '../services/audio';

interface CardItemProps {
  card: Card;
  onSelect: (card: Card) => void;
}

export const CardItem: React.FC<CardItemProps> = ({ card, onSelect }) => {
  const { favorites, toggleFavorite, updateCardQuantity, isReadOnly } = useCollection();
  const { t, getCardName, getCardSetName, language } = useLanguage();
  const isFavorite = favorites.includes(card.id);

  const cardName = getCardName(card);
  const setName = getCardSetName(card);

  const handleCardClick = () => {
    soundEffects.playClick();
    onSelect(card);
  };

  const handleQuantityDelta = (e: React.MouseEvent, delta: number) => {
    e.stopPropagation();
    if (isReadOnly) return;
    updateCardQuantity(card.id, delta);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isReadOnly) return;
    toggleFavorite(card.id);
  };

  return (
    <div 
      onClick={handleCardClick}
      className={`group relative min-w-0 overflow-hidden bg-pokedex-card/80 hover:bg-pokedex-card rounded-2xl p-2 sm:p-2.5 border transition-all duration-200 cursor-pointer shadow-md hover:shadow-pokedex-glow/20 flex flex-col justify-between ${
        card.quantity > 0 
          ? 'border-slate-700/80 hover:border-pokedex-blue/60' 
          : 'border-dashed border-red-500/30 opacity-75 grayscale-[40%]'
      }`}
    >
      {/* Card Visual / 3D Art */}
      <div className="aspect-[2.5/3.5] w-full relative mb-2">
        <HoloCard card={card} className="w-full h-full" />
        
        {/* Favorite Button / Indicator */}
        {(!isReadOnly || isFavorite) && (
          <button
            onClick={handleFavoriteClick}
            disabled={isReadOnly}
            aria-label={isFavorite ? t('cardItem.removeFavorite') : t('cardItem.addFavorite')}
            className={`absolute top-2 left-2 z-30 p-1.5 rounded-full backdrop-blur-md transition-transform ${
              !isReadOnly ? 'active:scale-90' : 'cursor-default'
            } ${
              isFavorite 
                ? 'bg-red-500/90 text-white shadow-md' 
                : 'bg-black/60 text-slate-300 hover:text-white'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
        )}

        {/* Chosen Deck Card Star Badge */}
        {card.is_deck_card && (
          <div className="absolute top-2 right-2 z-30 px-2 py-0.5 rounded-full bg-amber-400 text-slate-950 font-black text-[10px] font-mono shadow-md border border-yellow-200 flex items-center gap-1 backdrop-blur-md">
            <Star className="w-3 h-3 fill-current" />
            <span>Deck</span>
          </div>
        )}

        {/* Quantity Badge */}
        <div className={`absolute bottom-2 right-2 z-30 px-2 py-0.5 rounded-full text-xs font-mono font-black backdrop-blur-md border shadow-md flex items-center gap-1 ${
          card.quantity > 0 
            ? 'bg-slate-900/90 text-yellow-300 border-yellow-400/40' 
            : 'bg-red-950/90 text-red-300 border-red-500/40'
        }`}>
          <span>x{card.quantity}</span>
        </div>

        {/* Deck Badges */}
        {card.decks && card.decks.length > 0 && (
          <div className="absolute bottom-2 left-2 z-30 flex items-center space-x-1">
            <span className="bg-purple-900/90 border border-purple-400/40 text-purple-200 text-[10px] font-bold px-1.5 py-0.5 rounded-md flex items-center gap-1 shadow">
              <Layers className="w-2.5 h-2.5" />
              <span>{t('cardItem.deckPrefix')}{card.decks.map(d => d.replace('deck-', '')).join(',')}</span>
            </span>
          </div>
        )}
      </div>

      {/* Card Information */}
      <div className="space-y-1 min-w-0">
        <div className="flex items-start justify-between gap-1 min-w-0">
          <h3 className="font-bold text-xs text-white truncate group-hover:text-pokedex-cyan transition-colors min-w-0 flex-1" title={cardName}>
            {cardName}
          </h3>
          <span 
            className="text-[9px] sm:text-[10px] font-mono font-bold px-1.5 py-0.2 rounded border uppercase tracking-wider shrink-0 max-w-[48%] truncate"
            style={{ 
              backgroundColor: `${card.color_bg}22`,
              color: card.color_bg,
              borderColor: `${card.color_bg}55`
            }}
          >
            {card.color_name}
          </span>
        </div>

        <div className="flex items-center justify-between gap-1 text-[10px] text-slate-400 font-mono min-w-0">
          <span className="truncate min-w-0 flex-1">{setName}</span>
          <span className="bg-slate-800 px-1 rounded text-slate-300 shrink-0">#{card.card_number}</span>
        </div>

        {/* Price Tag if defined */}
        {card.price !== undefined && (
          <div className="flex items-center justify-between text-[10px] font-mono bg-yellow-400/10 px-1.5 py-0.5 rounded border border-yellow-400/25">
            <span className="text-yellow-400/80 flex items-center gap-1">
              <CircleDollarSign className="w-3 h-3 text-yellow-400" />
              <span>{t('nationalDex.price')}:</span>
            </span>
            <span className="text-yellow-300 font-bold">
              {language === 'pt' ? 'R$' : '$'} {card.price.toFixed(2)}
            </span>
          </div>
        )}

        {/* Quick Quantity Modifier */}
        <div className="pt-1.5 border-t border-slate-800/80 flex items-center justify-between">
          <span className="text-[10px] text-slate-400 font-mono">
            {card.rarity_name}
          </span>
          {!isReadOnly ? (
            <div className="flex items-center space-x-1" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={(e) => handleQuantityDelta(e, -1)}
                disabled={card.quantity <= 0}
                aria-label="Decrease quantity"
                className="w-6 h-6 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-30 text-slate-200 flex items-center justify-center text-xs active:scale-95 transition-all"
              >
                <Minus className="w-3 h-3" />
              </button>
              <span className="w-5 text-center font-mono font-bold text-xs text-yellow-300">
                {card.quantity}
              </span>
              <button
                onClick={(e) => handleQuantityDelta(e, 1)}
                aria-label="Increase quantity"
                className="w-6 h-6 rounded bg-pokedex-blue/80 hover:bg-pokedex-blue text-white flex items-center justify-center text-xs active:scale-95 transition-all shadow"
              >
                <Plus className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <span className="font-mono font-bold text-xs text-yellow-300">
              {card.quantity}x
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
